/**
 * Auth Manager Module
 * Handles user authentication and role-based access control
 */

const AuthManager = {
    currentUser: null,
    csrfToken: null,
    _epoch: 0,

    ROLES: {
        ADMIN: 'admin',
        COORDINATOR: 'coordinator',
        PROFESSOR: 'professor',
        RRHH: 'rrhh',
        STUDENT: 'student',
        WAREHOUSE: 'warehouse'
    },

    // Metadata only. Authentication and mutations always go through the server.
    users: [],

    hasModule(module, level = 'read') {
        const user = this.currentUser;
        if (!user?.professorId || user.studentId || !['coordinator','rrhh','professor','warehouse'].includes(user.role)) return false;
        module = {'calendario-formacion':'calendario','calificaciones-admin':'calificaciones','alumno-ficha':'alumnado','profesor-ficha':'profesores'}[module] || module;
        const grant = user.modulePermissions?.[module];
        if(module==='profesores' && this.hasModule('personal-laboral',level))return true;
        return grant === 'manage' || level === 'read' && grant === 'read';
    },

    canEditProfessorDirectory() {
        return ['admin','coordinator','rrhh'].includes(this.currentUser?.role) || this.hasModule('profesores','manage') || this.hasModule('personal-laboral','manage');
    },

    canManageModule(module, roles = ['admin','coordinator']) {
        return roles.includes(this.currentUser?.role) || this.hasModule(module,'manage');
    },

    requireModuleManage(module, roles = ['admin','coordinator']) {
        if(this.canManageModule(module,roles))return true;
        Utils.showToast('Tienes acceso de consulta. Administración debe habilitar Gestionar para modificar este módulo.','warning');
        return false;
    },

    clearCachedData() {
        this.users = [];
        if(typeof ProfessorDocs !== 'undefined')ProfessorDocs.ownerId=null;
        if (typeof WarehouseAPI !== 'undefined') WarehouseAPI.clear();
        if (typeof document !== 'undefined') {
            const adminActions = document.querySelector?.('.quick-create-container');
            if (adminActions) {
                adminActions.hidden = true;
                adminActions.style.display = 'none';
                adminActions.querySelector('.dropdown-menu')?.classList.remove('show');
                adminActions.querySelector('.quick-create-btn')?.setAttribute('aria-expanded', 'false');
            }
        }
        if (typeof CommunityUnread !== 'undefined') CommunityUnread.reset();
        BrowserState.clear();
        BrowserState.removeItem('ecpv_attendance');
        if (typeof FileStorage !== 'undefined') {
            for (const key of Object.keys(FileStorage.keyMapping)) BrowserState.removeItem(key);
            for (const key of Object.keys(FileStorage.fileMapping)) {
                FileStorage.dataStore[key] = Array.isArray(FileStorage.dataStore[key]) ? [] : {};
            }
            FileStorage.dataStore.currentUser = null;
        }
    },

    storeSession(result) {
        const { password, ...user } = result.user;
        this.currentUser = user;
        BrowserState.setItem('ecpv_current_user', JSON.stringify(user));
        this.csrfToken = result.csrfToken || this.csrfToken;
        this.markTabSession();
        if (typeof FileStorage !== 'undefined') FileStorage.setByPath('currentUser', user);
        this.watchSession();
    },

    watchSession() {
        if (this._sessionWatch || typeof window.setInterval !== 'function') return;
        this._sessionWatch = window.setInterval(async () => {
            const token = this.getCsrfToken();
            if (!token || this._checkingSession) return;
            this._checkingSession = true;
            try {
                const response = await fetch('/api/session', { headers: { 'X-SICAF-CSRF': token }, cache: 'no-store' });
                if (response.status === 401) { this.logoutSilently(); this.redirectToStart(); }
                else if (response.ok) {
                    const result = await response.json();
                    if (this.getCsrfToken() !== token) return;
                    if (JSON.stringify(result.user?.modulePermissions || {}) !== JSON.stringify(this.currentUser?.modulePermissions || {})) {
                        this.clearCachedData();
                        window.location.reload();
                        return;
                    }
                    if (this.isStudent() && typeof WarehouseAPI !== 'undefined') await WarehouseAPI.refreshCapabilities();
                }
            } catch { /* Network failures never authorize API access or restore a session. */ }
            finally { this._checkingSession = false; }
        }, 15000);
    },

    async init() {
        this.currentUser = null;
        this.clearCachedData();
        try {
            const response = await fetch('/api/session', {
                cache: 'no-store'
            });
            const result = await response.json();
            if (!response.ok || !result.success) {
                if (response.status === 401 || response.status === 403) this.logoutSilently();
                return false;
            }
            this.storeSession(result);
            return true;
        } catch {
            // No offline fallback to a cached administrator or decoded JWT.
            return false;
        }
    },

    async login(username, password) {
        try {
            const response = await fetch('/api/login', {
                method: 'POST', headers: { 'Content-Type': 'application/json', 'X-SICAF-Request':'1' },
                body: JSON.stringify({ username, password })
            });
            const result = await response.json();
            if (!response.ok || !result.success) return { success: false, error: result.error || 'No se pudo iniciar sesión' };
            this.clearCachedData();
            ++this._epoch;
            this.storeSession(result);
            this._activityWritten = 0; this.markActivity();
            this._channel?.postMessage('changed');
            return { success: true, user: this.currentUser };
        } catch {
            return { success: false, error: 'No se pudo conectar con el servidor' };
        }
    },

    logoutSilently() {
        if (this._sessionWatch) window.clearInterval(this._sessionWatch);
        this._sessionWatch = null;
        if (this._idleWatch) window.clearInterval(this._idleWatch);
        this._idleWatch = null;
        this.currentUser = null;
        this.csrfToken = null;
        try { window.sessionStorage.removeItem('sicaf-tab-key'); } catch { /* storage unavailable */ }
        ++this._epoch;
        this.clearCachedData();
        BrowserState.removeItem('ecpv_jwt_token');
    },

    // Brand curtain over the whole page (the reverse of the login reveal in App).
    // Animated only for the user's own logout; returns whether it is animating.
    coverScreen(animate) {
        if (typeof document === 'undefined' || typeof document.createElement !== 'function' || !document.body) return false;
        document.getElementById('ecpv-curtain')?.remove();
        const curtain = document.createElement('div');
        curtain.id = 'ecpv-curtain';
        curtain.className = 'ecpv-curtain ecpv-curtain-closing' + (animate ? ' ecpv-curtain-open' : '');
        curtain.setAttribute('aria-hidden', 'true');
        for (const side of ['left', 'right']) {
            const panel = document.createElement('div');
            panel.className = 'ecpv-curtain-panel ecpv-curtain-' + side;
            curtain.appendChild(panel);
        }
        document.body.appendChild(curtain);
        if (!animate) return false;
        curtain.getBoundingClientRect(); // paint it open, then close it
        curtain.classList.remove('ecpv-curtain-open');
        return true;
    },

    redirectToStart({ animate = false } = {}) {
        // Hide the private view and replace its history entry; a full navigation
        // also discards view state and pending UI callbacks. On the user's own
        // logout the curtain closes first; a session ended elsewhere leaves at once.
        const reduced = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const animating = this.coverScreen(animate && !reduced);
        const leave = () => {
            if (typeof document !== 'undefined' && document.body) document.body.style.visibility = 'hidden';
            // #ecpv-curtain keeps the page covered from its first paint (CSS :target).
            window.history.replaceState(null, '', '/#ecpv-curtain');
            window.location.reload();
        };
        if (animating) setTimeout(leave, 600); else leave();
    },

    async logout() {
        try {
            const response = await fetch('/api/logout', {method:'POST',headers:{'X-SICAF-CSRF':this.getCsrfToken()}});
            if (!response.ok && response.status !== 401) throw new Error('No se pudo cerrar la sesión en el servidor. Reinténtalo.');
        } catch {
            if (typeof Utils !== 'undefined') Utils.showToast('No se pudo cerrar la sesión. Comprueba la conexión y reinténtalo.','error');
            return false;
        }
        this.logoutSilently();
        this._channel?.postMessage('logout');

        this.redirectToStart({ animate: true });
        return true;
    },

    // Shared classroom computers (server/security/auth.js): except admin and rrhh, the session
    // belongs to the tab that signed in (a new tab or a reopened browser must sign in again) and
    // ends after 30 minutes without activity, with a warning one minute before. The tab key is
    // the CSRF value, kept only in this tab's sessionStorage; the server refuses requests without it.
    PERSISTENT_ROLES: ['admin', 'rrhh'],
    IDLE_MS: 30 * 60000,
    IDLE_WARNING_MS: 29 * 60000,
    sharedDevice() { return !!this.currentUser && !this.PERSISTENT_ROLES.includes(this.currentUser.role); },
    markTabSession() { try { if (this.csrfToken) window.sessionStorage.setItem('sicaf-tab-key', this.csrfToken); } catch { /* storage unavailable */ } },
    tabKey() { try { return window.sessionStorage.getItem('sicaf-tab-key'); } catch { return null; } },
    tabSessionValid() {
        if (!this.sharedDevice()) return true;
        return !!this.csrfToken && this.tabKey() === this.csrfToken;
    },
    async endSession(reason) {
        try { await fetch('/api/logout', { method: 'POST', headers: { 'X-SICAF-CSRF': this.getCsrfToken() } }); } catch { /* the server session still expires */ }
        this.logoutSilently();
        this._channel?.postMessage('logout');
        if (reason) try { window.sessionStorage.setItem('sicaf-session-ended', reason); } catch { /* notice only */ }
    },
    takeSessionEndedNotice() {
        try { const reason = window.sessionStorage.getItem('sicaf-session-ended'); window.sessionStorage.removeItem('sicaf-session-ended'); return reason; } catch { return null; }
    },
    lastActivity() {
        const shared = typeof BrowserState !== 'undefined' ? Number(BrowserState.getItem('sicaf_last_activity')) || 0 : 0;
        return Math.max(shared, this._lastActivity || 0);
    },
    markActivity() {
        const now = Date.now();
        this._lastActivity = now;
        if (now - (this._activityWritten || 0) < 15000) return; // other tabs read it; no need to write on every event
        this._activityWritten = now;
        if (typeof BrowserState !== 'undefined') BrowserState.setItem('sicaf_last_activity', String(now));
    },
    startIdleWatch() {
        if (!this.sharedDevice() || this._idleWatch || typeof window.setInterval !== 'function') return;
        this._activityWritten = 0;
        this.markActivity();
        if (!this._activityListening) {
            this._activityListening = true;
            const onActivity = () => this.markActivity();
            for (const type of ['pointerdown', 'keydown', 'wheel', 'touchstart', 'mousemove', 'scroll'])
                window.addEventListener(type, onActivity, { passive: true, capture: true });
        }
        this._idleWatch = window.setInterval(() => this.checkIdle(), 15000);
    },
    async checkIdle() {
        if (!this.sharedDevice()) return;
        const idle = Date.now() - this.lastActivity();
        if (idle >= this.IDLE_MS) {
            await this.endSession('idle');
            this.redirectToStart();
            return;
        }
        if (idle >= this.IDLE_WARNING_MS && !this._idleWarning && typeof SafeHTML !== 'undefined') {
            this._idleWarning = true;
            SafeHTML.fire({
                icon: 'warning', title: '¿Sigues ahí?',
                text: 'Por seguridad, tu sesión se cerrará en un minuto si no hay actividad.',
                confirmButtonText: 'Seguir conectado', timer: 60000, timerProgressBar: true
            }).then(result => { this._idleWarning = false; if (result?.isConfirmed) { this._activityWritten = 0; this.markActivity(); } });
        }
    },

    isLoggedIn() {
        return this.currentUser !== null;
    },

    getCurrentUser() {
        return this.currentUser;
    },

    getCsrfToken() {
        return this.csrfToken || this.tabKey();
    },

    hasRole(role) {
        return this.currentUser && this.currentUser.role === role;
    },

    isAdmin() {
        return this.hasRole(this.ROLES.ADMIN);
    },

    isCoordinator() {
        return this.hasRole(this.ROLES.COORDINATOR);
    },

    isProfessor() {
        return this.hasRole(this.ROLES.PROFESSOR);
    },

    isRRHH() {
        return this.hasRole(this.ROLES.RRHH);
    },

    isStudent() {
        return this.hasRole(this.ROLES.STUDENT);
    },

    isWarehouse() {
        return this.hasRole(this.ROLES.WAREHOUSE);
    },

    canManageWarehouse() {
        if(['admin','coordinator','warehouse'].includes(this.currentUser?.role))return true;
        if(this.hasModule('almacen','manage'))return true;
        const permission=typeof WarehouseAPI!=='undefined'?WarehouseAPI.capabilities:null;
        return this.isStudent() && permission?.canManage===true && (!permission.expiresAt || Date.parse(permission.expiresAt)>Date.now());
    },

    getProfessorId() {
        return this.currentUser ? this.currentUser.professorId : null;
    },

    getStudentId() {
        return this.currentUser ? this.currentUser.studentId : null;
    },

    canAccessView(viewName) {
        if (!this.isLoggedIn()) return false;
        if (this.isAdmin()) return true; // Admin can access everything
        if (this.hasModule(viewName)) return true;

        const role = this.currentUser.role;

        // Define access rules per view
        const accessRules = {
            'dashboard': ['admin', 'coordinator', 'rrhh', 'professor', 'student', 'warehouse'],
            'profesores': ['admin', 'coordinator', 'rrhh'],
            'formaciones': ['admin', 'coordinator'],
            'calendario': ['admin', 'coordinator', 'professor', 'student', 'warehouse'],
            'mensajes': ['admin', 'coordinator', 'rrhh', 'professor', 'student', 'warehouse'],
            'notificaciones': ['admin', 'coordinator', 'rrhh', 'professor', 'student', 'warehouse'],
            'ajustes': ['admin', 'coordinator', 'rrhh', 'professor', 'student', 'warehouse'],
            'documentos': ['admin', 'coordinator', 'rrhh', 'professor'], // Documents (general)
            'almacen': ['admin', 'warehouse', 'student'], // Warehouse module
            'justificantes': ['admin', 'coordinator', 'rrhh', 'professor', 'student'],
            'email-templates': ['admin', 'coordinator', 'rrhh']
        };

        const allowedRoles = accessRules[viewName];
        return allowedRoles && allowedRoles.includes(role);
    },

    canSeeSalaries() {
        if (!this.isLoggedIn()) return false;
        // Match the server's private payroll/staff-data policy.
        return this.isAdmin() || this.isRRHH() || this.hasModule('personal-laboral');
    },

    canManageUsers() {
        if (!this.isLoggedIn()) return false;
        return this.isAdmin();
    },

    async requestUser(path, method, data) {
        try {
            const response = await fetch(path, {
                method, headers: { 'Content-Type': 'application/json', 'X-SICAF-CSRF': this.getCsrfToken() },
                ...(data ? { body: JSON.stringify(data) } : {})
            });
            const result = await response.json();
            if (!response.ok || !result.success) return { success: false, message: result.error || 'No se pudo guardar la cuenta' };
            if (result.user) {
                this.users = this.users.filter(user => user.id !== result.user.id);
                this.users.push(result.user);
                if (result.user.id === this.currentUser?.id) this.storeSession(result);
            }
            return result;
        } catch {
            return { success: false, message: 'No se pudo conectar con el servidor' };
        }
    },

    async createUser(userData) {
        if (!this.canManageUsers()) return { success: false, message: 'Solo administración puede crear cuentas' };
        return this.requestUser('/api/users', 'POST', userData);
    },

    // Professor access by e-mail: creates the account (with this username) if missing and
    // sends the username, a new temporary password and the link. { success, sent, user }.
    async sendProfessorAccess(professorId, username) {
        if (!this.canManageUsers()) return { success: false, message: 'Solo administración da acceso' };
        return this.requestUser(`/api/professors/${encodeURIComponent(professorId)}/send-access`, 'POST', username ? { username } : {});
    },

    getUser(professorId) {
        return this.users.find(u => u.professorId === professorId);
    },

    getStudentUser(studentId) {
        return this.users.find(u => u.studentId === studentId);
    },

    async updateUserRole(professorId, newRole) {
        const user = this.getUser(professorId);
        if (!user) return { success: false, message: 'Usuario no encontrado' };
        return this.requestUser(`/api/users/${encodeURIComponent(user.id)}`, 'PUT', { role: newRole });
    },

    async updateUserPassword(id, newPassword, isProfessor = false) {
        const user = this.users.find(u => isProfessor ? u.professorId === id : u.studentId === id);
        if (!user) return { success: false, notFound: true, message: 'Usuario no encontrado' };
        return this.requestUser(`/api/users/${encodeURIComponent(user.id)}`, 'PUT', { password: newPassword });
    },

    async deleteUser(username) {
        const user = this.users.find(u => u.username === username);
        if (!user) return { success: false, message: 'Usuario no encontrado' };
        const result = await this.requestUser(`/api/users/${encodeURIComponent(user.id)}`, 'DELETE');
        if (result.success) this.users = this.users.filter(u => u.id !== user.id);
        return result;
    }
};

// Make AuthManager globally available
window.AuthManager = AuthManager;
if (typeof BroadcastChannel !== 'undefined') {
    AuthManager._channel = new BroadcastChannel('sicaf-session');
    AuthManager._channel.onmessage = event => {
        if (event.data === 'logout') {AuthManager.logoutSilently();AuthManager.redirectToStart();}
        else if (event.data === 'changed') {AuthManager.logoutSilently();window.location.reload();}
    };
}
